import React, { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Table, Card, Tag, Button, Space, Select, Input, Typography, Alert, Spin } from 'antd';
import { EyeOutlined, SearchOutlined } from '@ant-design/icons';
import { useStores } from '../../../stores';
import './PartnerOrders.scss';

const { Title } = Typography;
const { Option } = Select;

const PartnerOrdersList = observer(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { partnerOrderStore } = useStores();
  const { filteredOrders, loading, error, statusFilter, searchQuery } = partnerOrderStore;

  useEffect(() => {
    partnerOrderStore.fetchOrders();
  }, [partnerOrderStore]);

  const getStatusColor = (status) => {
    const colors = {
      PENDING: 'orange',
      CONFIRMED: 'blue',
      SHIPPED: 'purple',
      DELIVERED: 'green',
      CANCELLED: 'red'
    };
    return colors[status] || 'default';
  };

  const getStatusText = (status) => {
    const statusMap = {
      PENDING: t('partner.orders.status.pending'),
      CONFIRMED: t('partner.orders.status.confirmed'),
      PROCESSING: t('partner.orders.status.processing'),
      SHIPPED: t('partner.orders.status.shipped'),
      DELIVERED: t('partner.orders.status.delivered'),
      CANCELLED: t('partner.orders.status.cancelled')
    };
    return statusMap[status] || status;
  };

  const columns = [
    {
      title: t('partner.orders.tableHeaders.orderNumber'),
      dataIndex: 'id',
      key: 'id',
      width: 80,
      fixed: 'left',
      render: (id) => <strong>#{id}</strong>
    },
    {
      title: t('partner.orders.tableHeaders.customer'),
      key: 'customer',
      width: 180,
      ellipsis: true,
      render: (_, record) => (
        <div style={{ overflow: 'hidden' }}>
          <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{record.user_first_name} {record.user_last_name}</div>
          <div style={{ fontSize: 12, color: '#666', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{record.user_email}</div>
        </div>
      )
    },
    {
      title: t('partner.orders.tableHeaders.date'),
      dataIndex: 'created_at',
      key: 'created_at',
      width: 100,
      render: (date) => new Date(date).toLocaleDateString()
    },
    {
      title: t('partner.orders.tableHeaders.items'),
      dataIndex: 'items',
      key: 'items',
      width: 70,
      align: 'center',
      render: (items) => items.length
    },
    {
      title: t('partner.orders.tableHeaders.total'),
      dataIndex: 'total_amount',
      key: 'total_amount',
      width: 100,
      align: 'right',
      render: (amount) => `€${parseFloat(amount).toFixed(2)}`
    },
    {
      title: t('common.status'),
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (status) => (
        <Tag color={getStatusColor(status)}>
          {getStatusText(status)}
        </Tag>
      )
    },
    {
      title: t('common.actions'),
      key: 'actions',
      width: 90,
      fixed: 'right',
      align: 'center',
      render: (_, record) => (
        <Button
          type="link"
          size="small"
          icon={<EyeOutlined />}
          onClick={() => navigate(`/partner/orders/${record.id}`)}
        >
          {t('common.view')}
        </Button>
      )
    }
  ];

  return (
    <div className="partner-orders-list">
      <div className="page-header">
        <Title level={2}>{t('partner.orders.title')}</Title>
      </div>

      {error && (
        <Alert
          message={t('common.error')}
          description={error}
          type="error"
          closable
          style={{ marginBottom: 16 }}
        />
      )}

      <Card>
        {/* Filters */}
        <Space size="middle" style={{ marginBottom: 16 }} wrap>
          <Select
            placeholder={t('partner.orders.filterByStatus')}
            allowClear
            style={{ width: 180 }}
            value={statusFilter}
            onChange={(value) => partnerOrderStore.setStatusFilter(value)}
          >
            <Option value="PENDING">{t('partner.orders.status.pending')}</Option>
            <Option value="CONFIRMED">{t('partner.orders.status.confirmed')}</Option>
            <Option value="SHIPPED">{t('partner.orders.status.shipped')}</Option>
            <Option value="DELIVERED">{t('partner.orders.status.delivered')}</Option>
            <Option value="CANCELLED">{t('partner.orders.status.cancelled')}</Option>
          </Select>

          <Input
            placeholder={t('partner.orders.searchPlaceholder')}
            prefix={<SearchOutlined />}
            allowClear
            style={{ width: 250 }}
            value={searchQuery}
            onChange={(e) => partnerOrderStore.setSearchQuery(e.target.value)}
          />
        </Space>

        {/* Orders Table */}
        <Table
          columns={columns}
          dataSource={filteredOrders}
          rowKey="id"
          loading={loading}
          scroll={{ x: 800 }}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `${total} ${t('partner.orders.title')}`
          }}
          locale={{
            emptyText: t('partner.orders.noOrders')
          }}
        />
      </Card>
    </div>
  );
});

export default PartnerOrdersList;
